import React, { useState } from 'react'
import { supabase } from '../supabaseClient'
import { useTheme } from '../ThemeContext'

export default function SMSConsentGate({ user, existingPhone, onConsent }) {
  const { darkMode } = useTheme()
  const [phone, setPhone] = useState(existingPhone || '')
  const [agreed, setAgreed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const bg         = darkMode ? '#0d1b2e' : '#f0f4f8'
  const cardBg     = darkMode ? '#111f33' : '#ffffff'
  const border     = darkMode ? 'rgba(90,127,173,0.2)' : 'rgba(61,100,148,0.15)'
  const textPrimary   = darkMode ? '#e2eaf4' : '#1a2a3e'
  const textSecondary = darkMode ? '#7aa0c8' : '#4a6080'
  const inputBg    = darkMode ? 'rgba(20,36,58,0.7)' : '#f8fafc'
  const accent     = '#3d6494'
  const accentHover = '#2d5080'

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!agreed || !phone.trim()) return

    setSaving(true)
    setError('')

    try {
      const { error: updateError, count } = await supabase
        .from('users')
        .update({
          phone_number: phone.trim(),
          sms_consent: true,
          sms_consent_at: new Date().toISOString(),
        })
        .eq('id', user.id)
        .select('id', { count: 'exact', head: true })

      if (updateError) throw updateError
      if (count === 0) throw new Error('No matching user record found')
      onConsent()
    } catch (err) {
      setError('Unable to save. Please try again.')
      setSaving(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: bg,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '1.5rem',
      paddingTop: 'max(1.5rem, env(safe-area-inset-top))',
      overflowY: 'auto',
      fontFamily: "'Source Sans 3', system-ui, sans-serif",
    }}>
      <div style={{
        width: '100%',
        maxWidth: '440px',
        margin: 'auto 0',
        backgroundColor: cardBg,
        border: `1px solid ${border}`,
        borderRadius: '14px',
        padding: '2.5rem 2rem',
        boxShadow: darkMode
          ? '0 8px 40px rgba(0,0,0,0.5)'
          : '0 4px 24px rgba(61,100,148,0.1)',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            backgroundColor: darkMode ? 'rgba(61,100,148,0.2)' : 'rgba(61,100,148,0.08)',
            border: `1px solid ${border}`,
            borderRadius: '9999px',
            padding: '0.3rem 0.85rem',
            marginBottom: '1.25rem',
          }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: accent, display: 'inline-block' }} />
            <span style={{ fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: textSecondary }}>
              Account Setup
            </span>
          </div>
          <h1 style={{ margin: '0 0 0.6rem', fontSize: '1.6rem', fontWeight: '700', color: textPrimary, lineHeight: 1.2 }}>
            Enable SMS Notifications
          </h1>
          <p style={{ margin: 0, fontSize: '0.9rem', color: textSecondary, lineHeight: 1.6 }}>
            Stay informed with shift assignments, on-call alerts, and complaint notifications sent directly to your device.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Phone number */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.72rem',
              fontWeight: '700',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: textSecondary,
              marginBottom: '0.5rem',
            }}>
              Mobile Number
            </label>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="(919) 555-0000"
              required
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                backgroundColor: inputBg,
                border: `1px solid ${border}`,
                borderRadius: '8px',
                fontSize: '1rem',
                color: textPrimary,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Consent box */}
          <div style={{
            backgroundColor: darkMode ? 'rgba(61,100,148,0.08)' : 'rgba(61,100,148,0.04)',
            border: `1px solid ${border}`,
            borderRadius: '10px',
            padding: '1rem',
            marginBottom: '1.5rem',
          }}>
            <label style={{ display: 'flex', gap: '0.75rem', cursor: 'pointer', alignItems: 'flex-start' }}>
              <div style={{ position: 'relative', flexShrink: 0, marginTop: '2px' }}>
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={e => setAgreed(e.target.checked)}
                  style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                />
                <div style={{
                  width: 20,
                  height: 20,
                  borderRadius: '5px',
                  backgroundColor: agreed ? accent : inputBg,
                  border: `2px solid ${agreed ? accent : border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s',
                }}>
                  {agreed && (
                    <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                      <path d="M1 4L4.5 7.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </div>
              <span style={{ fontSize: '0.88rem', color: textPrimary, lineHeight: 1.5 }}>
                I agree to receive operational SMS notifications from <strong>ABCAxis</strong>. Frequency: 1–5 messages/week. Msg & data rates may apply. Reply STOP to opt out, HELP for help.
              </span>
            </label>

            <div style={{
              marginTop: '0.875rem',
              paddingTop: '0.875rem',
              borderTop: `1px solid ${border}`,
              fontSize: '0.78rem',
              color: textSecondary,
              lineHeight: 1.6,
            }}>
              Message frequency varies (1–5 messages/week). Msg & data rates may apply. Reply <strong style={{ color: textPrimary }}>STOP</strong> to opt out at any time. Reply <strong style={{ color: textPrimary }}>HELP</strong> for assistance. View our{' '}
              <a href="https://abcaxis.com/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: accent }}>
                Privacy Policy
              </a>
              .
            </div>
          </div>

          {error && (
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#ef4444', textAlign: 'center' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={!agreed || !phone.trim() || saving}
            style={{
              width: '100%',
              padding: '0.875rem',
              backgroundColor: agreed && phone.trim() ? accent : (darkMode ? 'rgba(61,100,148,0.2)' : '#e2e8f0'),
              color: agreed && phone.trim() ? '#ffffff' : textSecondary,
              border: 'none',
              borderRadius: '9px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: agreed && phone.trim() ? 'pointer' : 'not-allowed',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={e => { if (agreed && phone.trim()) e.target.style.backgroundColor = accentHover }}
            onMouseLeave={e => { if (agreed && phone.trim()) e.target.style.backgroundColor = accent }}
          >
            {saving ? 'Saving…' : 'Continue to ABCAxis →'}
          </button>
        </form>
      </div>
    </div>
  )
}
